import { useEffect, useState } from 'react';
import { legacyTenantService } from '../services/api';
import './LegacyTenants.css';

const STATUS_LABEL = {
  REGISTERED: 'Registrato',
  EMAIL_VERIFIED: 'Email Verificata',
  ONBOARDING_STARTED: 'Onboarding',
  CONSENTS_ACCEPTED: 'Consensi OK',
  CONTRACT_SIGNED_PENDING_ACTIVATION: 'In attesa attivazione',
  ACTIVE: 'Attivo',
  SUSPENDED: 'Sospeso',
  CLOSED: 'Chiuso',
};

const STATUS_COLOR = {
  ACTIVE: '#28a745',
  SUSPENDED: '#dc3545',
  CLOSED: '#6c757d',
};

function StatusBadge({ status }) {
  return (
    <span style={{
      background: STATUS_COLOR[status] || '#6c757d',
      color: '#fff',
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: 12,
      fontWeight: 600,
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function centsToEur(cents) {
  return (Number(cents || 0) / 100).toFixed(2);
}

function eurToCents(eur) {
  return Math.round(parseFloat(eur || '0') * 100);
}

function featureLimitToFormValue(value) {
  if (value === null || value === undefined || value === '') return '';
  return Number.isFinite(Number(value)) ? String(value) : '';
}

function formValueToFeatureLimit(value) {
  const rawValue = String(value ?? '').trim();
  return rawValue === '' ? null : Math.max(0, parseInt(rawValue, 10) || 0);
}

function buildEmptyForm(featureDefinitions = []) {
  const featureValues = Object.fromEntries(featureDefinitions.map(definition => [
    definition.key,
    definition.valueType === 'BOOLEAN' ? false : '',
  ]));
  return {
    ...featureValues,
    feePercent: '0',
    feeFixedEur: '0.00',
    applyLegacyContractFee: false,
    notes: '',
  };
}

function recordToForm(features, feePercent, feeFixedCents, notes, featureDefinitions = []) {
  const featureValues = Object.fromEntries(featureDefinitions.map(definition => [
    definition.key,
    definition.valueType === 'BOOLEAN'
      ? Boolean(features?.[definition.key])
      : featureLimitToFormValue(features?.[definition.key]),
  ]));
  return {
    ...featureValues,
    feePercent: String(feePercent ?? '0'),
    feeFixedEur: centsToEur(feeFixedCents),
    applyLegacyContractFee: false,
    notes: notes || '',
  };
}

function formToPayload(form, featureDefinitions) {
  return {
    features: Object.fromEntries(featureDefinitions.map(definition => [
      definition.key,
      definition.valueType === 'BOOLEAN'
        ? Boolean(form[definition.key])
        : formValueToFeatureLimit(form[definition.key]),
    ])),
    feePercent: parseInt(form.feePercent || '0', 10),
    feeFixedCents: eurToCents(form.feeFixedEur),
    applyLegacyContractFee: Boolean(form.applyLegacyContractFee),
    notes: form.notes || null,
  };
}

function LimitField({ label, value, onChange }) {
  const isUnlimited = value === '' || Number(value) === 0;
  return (
    <div className="lt-number-row">
      <label>
        <span>{label}</span>
        <input
          type="number"
          min="0"
          step="1"
          placeholder="Illimitato"
          value={value}
          onChange={event => onChange(event.target.value)}
        />
      </label>
      <small className={`lt-number-help${isUnlimited ? ' unlimited' : ''}`}>
        {isUnlimited ? 'Valore attuale: Illimitato (0 o campo vuoto)' : '0 o campo vuoto = Illimitato'}
      </small>
    </div>
  );
}

function LegacyConfigurationFields({ form, setForm, featureDefinitions, contractFeeAvailable = true, contractFeeMessage }) {
  const updateField = (key, value) => setForm(previous => ({ ...previous, [key]: value }));

  return (
    <>
      <section className="lt-section">
        <h3>Feature</h3>
        {featureDefinitions.filter(definition => definition.valueType === 'BOOLEAN').map(definition => (
          <label key={definition.key} className="lt-toggle-row" title={definition.description || undefined}>
            <span>{definition.name}</span>
            <div
              className={`lt-toggle${form[definition.key] ? ' on' : ''}`}
              onClick={() => updateField(definition.key, !form[definition.key])}
            >
              <div className="lt-toggle-thumb" />
            </div>
          </label>
        ))}

        {featureDefinitions.filter(definition => definition.valueType !== 'BOOLEAN').map(definition => (
          <div key={definition.key} title={definition.description || undefined}>
            <LimitField
              label={definition.name}
              value={form[definition.key] ?? ''}
              onChange={value => updateField(definition.key, value)}
            />
          </div>
        ))}
      </section>

      <section className="lt-section">
        <h3>Fee SoonTip</h3>
        <label className={`lt-contract-fee-option${!contractFeeAvailable ? ' disabled' : ''}`}>
          <input
            type="checkbox"
            checked={form.applyLegacyContractFee}
            disabled={!contractFeeAvailable}
            onChange={event => updateField('applyLegacyContractFee', event.target.checked)}
          />
          <span>Applica fee del contratto legacy sottoscritto</span>
        </label>
        {contractFeeMessage && (
          <div className={`lt-contract-fee-message${contractFeeAvailable ? '' : ' unavailable'}`}>
            {contractFeeMessage}
          </div>
        )}
        <div className="lt-fee-row">
          <label>
            <span>Percentuale (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              disabled={form.applyLegacyContractFee}
              value={form.feePercent}
              onChange={event => updateField('feePercent', event.target.value)}
            />
          </label>
          <label>
            <span>Fisso (EUR)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              disabled={form.applyLegacyContractFee}
              value={form.feeFixedEur}
              onChange={event => updateField('feeFixedEur', event.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="lt-section">
        <h3>Note</h3>
        <textarea
          className="lt-notes"
          rows={3}
          placeholder="Note interne (opzionale)"
          value={form.notes}
          onChange={event => updateField('notes', event.target.value)}
        />
      </section>
    </>
  );
}

const LegacyTenants = () => {
  const [tenants, setTenants] = useState([]);
  const [featureDefinitions, setFeatureDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(buildEmptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkForm, setBulkForm] = useState(buildEmptyForm());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkSuccess, setBulkSuccess] = useState(null);
  const [bulkScope, setBulkScope] = useState('UNCONFIGURED');
  const [bulkLimit, setBulkLimit] = useState('50');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await legacyTenantService.list();
      setTenants(res.data || []);
      setFeatureDefinitions(res.featureDefinitions || []);
      setBulkForm(previous => recordToForm({}, previous.feePercent, eurToCents(previous.feeFixedEur), previous.notes, res.featureDefinitions || []));
    } catch (err) {
      setError('Errore nel caricamento dei tenant legacy.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectedTenant = tenants.find(t => t.id === selectedId);
  const selectedContractFeeAvailable = Boolean(
    selectedTenant?.contractFeePercent != null && selectedTenant?.contractFeeFixedCents != null
  );
  const parsedBulkLimit = Number(bulkLimit);
  const validBulkLimit = Number.isInteger(parsedBulkLimit) && parsedBulkLimit >= 1 && parsedBulkLimit <= 500;
  const bulkEligibleTenants = bulkScope === 'UNCONFIGURED'
    ? tenants.filter(tenant => !tenant.configured)
    : tenants;
  const bulkTargetTenants = validBulkLimit
    ? bulkEligibleTenants.slice(0, parsedBulkLimit)
    : [];
  const tenantsWithoutContractFee = bulkTargetTenants.filter(tenant => (
    tenant.contractFeePercent == null || tenant.contractFeeFixedCents == null
  )).length;

  const handleSelect = (tenant) => {
    setSelectedId(tenant.id);
    setSaveError(null);
    setSaveSuccess(null);
    if (tenant.features) {
      setForm(recordToForm(tenant.features, tenant.feePercent, tenant.feeFixedCents, tenant.notes, featureDefinitions));
    } else {
      setForm(buildEmptyForm(featureDefinitions));
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const result = await legacyTenantService.save(selectedId, formToPayload(form, featureDefinitions));
      const savedFeatures = result.data.features;
      setSaveSuccess('Configurazione salvata.');
      setTenants(prev => prev.map(t =>
        t.id === selectedId
          ? {
              ...t,
              features: savedFeatures,
              feePercent: result.data.feePercent,
              feeFixedCents: result.data.feeFixedCents,
              notes: result.data.notes,
              configured: true,
            }
          : t
      ));
      setForm(recordToForm(savedFeatures, result.data.feePercent, result.data.feeFixedCents, result.data.notes, featureDefinitions));
    } catch (err) {
      setSaveError('Errore durante il salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSave = async () => {
    if (bulkTargetTenants.length === 0) return;
    const scopeLabel = bulkScope === 'UNCONFIGURED' ? 'non configurati' : 'selezionati tra tutti';
    const confirmed = window.confirm(
      `Applicare questa configurazione a ${bulkTargetTenants.length} tenant legacy ${scopeLabel}? Le eventuali configurazioni esistenti verranno sostituite.`
    );
    if (!confirmed) return;

    setBulkSaving(true);
    setBulkError(null);
    setBulkSuccess(null);
    const payload = {
      ...formToPayload(bulkForm, featureDefinitions),
      scope: bulkScope,
      limit: parsedBulkLimit,
    };
    try {
      const result = await legacyTenantService.saveBulk(payload);
      const refreshed = await legacyTenantService.list();
      const refreshedTenants = refreshed.data || [];
      setTenants(refreshedTenants);
      if (selectedId) {
        const refreshedSelection = refreshedTenants.find(tenant => tenant.id === selectedId);
        if (refreshedSelection) {
          setForm(recordToForm(
            refreshedSelection.features,
            refreshedSelection.feePercent,
            refreshedSelection.feeFixedCents,
            refreshedSelection.notes,
            featureDefinitions
          ));
        }
      }
      setSaveError(null);
      setSaveSuccess(null);
      const remainingMessage = result.remainingCount > 0
        ? ` Restano ${result.remainingCount} tenant idonei non elaborati per il limite impostato.`
        : '';
      const fallbackSummary = result?.feeResolution?.requestedContractFee === true
        ? (() => {
            const fallbackCount = Number(result?.feeResolution?.fallbackAppliedCount || 0);
            if (fallbackCount <= 0) {
              return ' Fee contrattuale applicata da contratto legacy per tutti i tenant elaborati.';
            }
            const bySource = result?.feeResolution?.fallbackBySource || {};
            const venueCount = Number(bySource.venueSettings || 0);
            const globalCount = Number(bySource.globalSettings || 0);
            const envCount = Number(bySource.envEmergency || 0);
            return ` Fallback fee applicato per ${fallbackCount} tenant (venue settings: ${venueCount}, global settings: ${globalCount}, env emergency: ${envCount}).`;
          })()
        : '';
      setBulkSuccess(`Configurazione applicata a ${result.updatedCount} tenant legacy.${remainingMessage}${fallbackSummary}`);
    } catch (err) {
      setBulkError(err?.response?.data?.error || 'Errore durante la configurazione bulk.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId || !window.confirm('Rimuovere la configurazione? Il tenant avrà accesso completo legacy.')) return;
    setDeleting(true);
    try {
      await legacyTenantService.deleteConfig(selectedId);
      setTenants(prev => prev.map(t =>
        t.id === selectedId ? { ...t, features: null, notes: null, configured: false } : t
      ));
      setForm(buildEmptyForm(featureDefinitions));
      setSaveSuccess('Configurazione rimossa. Il tenant non ha più accesso a nessuna feature.');
    } catch (err) {
      setSaveError('Errore durante la rimozione.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="legacy-tenants-page">
      <div className="lt-header">
        <h1>Tenant Legacy Features</h1>
        <p className="lt-subtitle">
          Tenant con contratto firmato (senza piano abbonamento). Configura le feature e la fee per ciascuno.
        </p>
      </div>

      <details className="lt-guide">
        <summary>Come funziona questa sezione?</summary>
        <div className="lt-guide-body">
          <h4>Chi è un "tenant legacy"?</h4>
          <p>
            Un tenant legacy è un cliente che ha firmato un contratto SoonTip <strong>prima</strong> che
            venisse introdotto il sistema dei piani abbonamento. Non ha una <code>TenantSubscription</code>
            attiva, ma ha almeno un record <code>ContractAcceptance</code> nel database.
            Sono i clienti storici che pagavano tramite condizioni contrattuali custom (fee %, canone fisso, ecc.).
          </p>

          <h4>Come il codice li identifica</h4>
          <p>
            La funzione <code>getTenantFeatures(tenantId)</code> in{' '}
            <code>backend/src/utils/tenantFeatures.js</code> segue questo ordine:
          </p>
          <ol>
            <li>Cerca una <code>TenantSubscription</code> con <code>normalizedStatus = 'ACTIVE'</code> → se esiste, usa le feature del piano.</li>
            <li>Se non esiste subscription, cerca un record <code>LegacyTenantFeature</code> per quel tenant → se esiste, usa quelle feature.</li>
            <li>Se nessuno dei due esiste, restituisce <code>{'{}'}</code> → nessuna feature abilitata.</li>
          </ol>
          <p>
            I tenant legacy visibili in questa lista sono quelli che compaiono nella lista tramite <code>ContractAcceptance</code>,
            ma la loro configurazione feature è gestita esclusivamente da questa pagina tramite la tabella <code>LegacyTenantFeature</code>. Puoi configurare un singolo tenant oppure usare la configurazione bulk per applicare gli stessi valori a tutti i tenant elencati.
          </p>

          <h4>Cosa succede se un tenant legacy non ha una configurazione?</h4>
          <p>
            Se non hai ancora salvato una configurazione (badge <strong>⚠ Non configurato</strong> nella lista),{' '}
            <code>getTenantFeatures</code> restituisce <code>{'{}'}</code> e il tenant non ha accesso
            a nessuna feature: i gate backend ritornano 403 e i toggle nel dashboard appaiono disabilitati.
            <strong> Devi configurare esplicitamente le feature, singolarmente oppure tramite il comando bulk.</strong>
          </p>

          <h4>Configurazione bulk</h4>
          <p>
            Il comando bulk applica lo stesso insieme completo di feature, limiti e note ai tenant inclusi. Scegli se elaborare soltanto i tenant non configurati oppure tutti, quindi imposta il numero massimo per esecuzione: il filtro viene applicato prima del limite. Con la fee manuale applica anche la stessa fee; selezionando <strong>Applica fee del contratto legacy sottoscritto</strong>, invece, ogni tenant riceve la fee specifica memorizzata nel proprio contratto. L'operazione sostituisce integralmente eventuali configurazioni individuali precedenti ed è eseguita in un'unica transazione.
          </p>

          <h4>Fee SoonTip</h4>
          <p>
            I campi economici <code>feePercent</code> e <code>feeFixedCents</code> sono separati dal JSON delle feature e vengono letti da <code>getPlanFeeForTenant()</code> per il calcolo delle commissioni al posto della fee globale (<code>GlobalSettings</code>). Puoi inserire una fee manuale oppure selezionare il checkbox per copiare percentuale e importo fisso dallo snapshot dell'ultimo contratto legacy sottoscritto.
          </p>
        </div>
      </details>

      {error && <div className="lt-error">{error}</div>}

      <details className="lt-bulk-panel">
        <summary>Configurazione bulk per tutti i tenant legacy</summary>
        <div className="lt-bulk-body">
          <div className="lt-bulk-warning">
            I valori salvati qui sostituiscono integralmente feature, limiti, fee e note dei tenant inclusi dal filtro e dal limite.
          </div>
          <div className="lt-bulk-controls">
            <label>
              <span>Applica a</span>
              <select
                value={bulkScope}
                onChange={event => {
                  setBulkScope(event.target.value);
                  setBulkForm(previous => ({ ...previous, applyLegacyContractFee: false }));
                  setBulkSuccess(null);
                }}
              >
                <option value="UNCONFIGURED">Solo tenant non configurati</option>
                <option value="ALL">Tutti i tenant legacy</option>
              </select>
            </label>
            <label>
              <span>Limite massimo</span>
              <input
                type="number"
                min="1"
                max="500"
                step="1"
                value={bulkLimit}
                onChange={event => {
                  setBulkLimit(event.target.value);
                  setBulkForm(previous => ({ ...previous, applyLegacyContractFee: false }));
                  setBulkSuccess(null);
                }}
              />
            </label>
            <div className="lt-bulk-target-count">
              {validBulkLimit
                ? <><strong>{bulkTargetTenants.length}</strong> destinatari su {bulkEligibleTenants.length} idonei</>
                : <strong>Inserisci un limite intero tra 1 e 500</strong>}
            </div>
          </div>
          <LegacyConfigurationFields
            form={bulkForm}
            setForm={setBulkForm}
            featureDefinitions={featureDefinitions}
            contractFeeAvailable={true}
            contractFeeMessage={tenantsWithoutContractFee === 0
              ? 'Ogni tenant riceverà la fee memorizzata nel proprio contratto legacy sottoscritto.'
              : `Fee contrattuale non disponibile per ${tenantsWithoutContractFee} tenant: verrà applicato fallback automatico (prima venue settings, poi global settings, infine ENV emergency) solo per quei tenant.`}
          />
          {bulkError && <div className="lt-error">{bulkError}</div>}
          {bulkSuccess && <div className="lt-success">{bulkSuccess}</div>}
          <button className="lt-btn-bulk" onClick={handleBulkSave} disabled={bulkSaving || bulkTargetTenants.length === 0}>
            {bulkSaving ? 'Applicazione in corso…' : `Applica a ${bulkTargetTenants.length} tenant legacy`}
          </button>
        </div>
      </details>

      <div className="lt-layout">
        {/* Left: tenant list */}
        <div className="lt-list">
          {loading && <div className="lt-loading">Caricamento…</div>}
          {!loading && tenants.length === 0 && (
            <div className="lt-empty">Nessun tenant legacy trovato.</div>
          )}
          {tenants.map(t => (
            <div
              key={t.id}
              className={`lt-list-item${selectedId === t.id ? ' selected' : ''}`}
              onClick={() => handleSelect(t)}
            >
              <div className="lt-list-name">
                {t.company_name || '—'}
                {t.adminAlias && <span className="lt-list-alias"> ({t.adminAlias})</span>}
              </div>
              {t.ownerEmail && <div className="lt-list-email">{t.ownerEmail}</div>}              {(t.contractFeePercent != null || t.contractFeeFixedCents != null) && (
                <div className="lt-list-fee">
                  Fee contratto: {t.contractFeePercent ?? 0}% + €{centsToEur(t.contractFeeFixedCents ?? 0)}
                  {t.contractOfferLabel && <span className="lt-list-fee-label"> — {t.contractOfferLabel}</span>}
                </div>
              )}              <div className="lt-list-meta">
                <StatusBadge status={t.status} />
                {t.configured
                  ? <span className="lt-badge-configured">Configurato</span>
                  : <span className="lt-badge-fallback">⚠ Non configurato</span>
                }
              </div>
            </div>
          ))}
        </div>

        {/* Right: feature editor */}
        <div className="lt-detail">
          {!selectedTenant && (
            <div className="lt-empty-detail">Seleziona un tenant per configurare le feature.</div>
          )}
          {selectedTenant && (
            <>
              <div className="lt-detail-title">
                <strong>{selectedTenant.company_name}</strong>
                <StatusBadge status={selectedTenant.status} />
              </div>
              {!selectedTenant.configured && (
                <div className="lt-warning">
                  <strong>Nessuna configurazione salvata.</strong> Il tenant non ha accesso a nessuna feature finché non salvi una configurazione.
                </div>
              )}

              <LegacyConfigurationFields
                form={form}
                setForm={setForm}
                featureDefinitions={featureDefinitions}
                contractFeeAvailable={selectedContractFeeAvailable}
                contractFeeMessage={selectedContractFeeAvailable
                  ? `Fee contrattuale: ${selectedTenant.contractFeePercent}% + €${centsToEur(selectedTenant.contractFeeFixedCents)}`
                  : 'Fee del contratto legacy non disponibile per questo tenant.'}
              />

              {saveError && <div className="lt-error">{saveError}</div>}
              {saveSuccess && <div className="lt-success">{saveSuccess}</div>}

              <div className="lt-actions">
                <button className="lt-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvataggio…' : 'Salva configurazione'}
                </button>
                {selectedTenant.configured && (
                  <button className="lt-btn-delete" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Rimozione…' : 'Rimuovi configurazione'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegacyTenants;
